/**
 * CSV encode / decode helpers for test-kit log import/export.
 *
 * RFC 4180-compliant encoding.  Tolerant decoding that returns both
 * successfully-parsed entries and per-row error messages.  Photos are
 * intentionally excluded from CSV — they live in localStorage only.
 */
const HEADER = 'timestamp,kitSlug,brand,testName,parameter,parameterKey,result,unit,notes';
const REQUIRED_COLUMNS = [
    'timestamp',
    'kitSlug',
    'parameter',
    'result',
    'unit',
];
// ---------------------------------------------------------------------------
// Encoding
// ---------------------------------------------------------------------------
function csvEscape(value) {
    if (value.includes(',') ||
        value.includes('"') ||
        value.includes('\n') ||
        value.includes('\r')) {
        return '"' + value.replace(/"/g, '""') + '"';
    }
    return value;
}
/**
 * Encode an array of log entries to an RFC 4180 CSV string.
 *
 * - Header is the first line.
 * - Fields containing a comma, double-quote, or newline are quoted.
 * - Internal quotes are doubled.
 * - Rows are terminated with CRLF.
 */
export function encodeLogsToCsv(logs) {
    const rows = logs.map((log) => [
        csvEscape(log.date),
        csvEscape(log.kitSlug),
        csvEscape(log.brand),
        csvEscape(log.testName),
        csvEscape(log.parameter),
        csvEscape(log.parameterKey),
        String(log.result),
        csvEscape(log.unit),
        csvEscape(log.notes ?? ''),
    ].join(','));
    return [HEADER, ...rows].join('\r\n') + '\r\n';
}
// ---------------------------------------------------------------------------
// Decoding
// ---------------------------------------------------------------------------
function parseCsvLine(line) {
    const fields = [];
    let i = 0;
    while (i < line.length) {
        if (line[i] === '"') {
            // Quoted field
            i++; // skip opening quote
            let field = '';
            while (i < line.length) {
                if (line[i] === '"') {
                    if (i + 1 < line.length && line[i + 1] === '"') {
                        // Escaped quote
                        field += '"';
                        i += 2;
                    }
                    else {
                        // Closing quote
                        i++;
                        break;
                    }
                }
                else {
                    field += line[i];
                    i++;
                }
            }
            fields.push(field);
            // Skip optional comma after closing quote
            if (i < line.length && line[i] === ',')
                i++;
        }
        else {
            // Unquoted field
            let field = '';
            while (i < line.length && line[i] !== ',') {
                field += line[i];
                i++;
            }
            fields.push(field);
            if (i < line.length)
                i++; // skip comma
        }
    }
    return fields;
}
/**
 * Parse a CSV string into log entries + per-row error messages.
 *
 * The first line MUST match the expected header exactly (no leading BOM
 * is expected).  Each subsequent row is parsed independently; rows with
 * missing required columns are reported in `errors` and skipped.
 *
 * @returns An object with `entries` (valid imported entries, each with
 *          a freshly-generated id) and `errors` (one string per skipped row).
 */
/**
 * Split a CSV string into logical record lines, respecting multi-line
 * quoted fields (RFC 4180).
 */
function splitCsvRecords(csv) {
    const records = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < csv.length; i++) {
        const ch = csv[i];
        if (ch === '"') {
            inQuotes = !inQuotes;
        }
        if (ch === '\r' && !inQuotes) {
            // CR — skip, the next char should be \n
            records.push(current);
            current = '';
            if (i + 1 < csv.length && csv[i + 1] === '\n')
                i++; // skip LF
        }
        else if (ch === '\n' && !inQuotes) {
            // LF-only line ending
            records.push(current);
            current = '';
        }
        else {
            current += ch;
        }
    }
    // Don't forget the last record
    if (current.length > 0) {
        records.push(current);
    }
    return records;
}
export function decodeLogsFromCsv(csv) {
    const entries = [];
    const errors = [];
    // Split respecting quoted multi-line fields
    const lines = splitCsvRecords(csv);
    // Skip trailing empty line
    const lastLine = lines.length > 0 ? lines[lines.length - 1] : undefined;
    if (lastLine !== undefined && lastLine.trim() === '') {
        lines.pop();
    }
    if (lines.length === 0) {
        return { entries, errors };
    }
    const headerLine = lines[0];
    if (!headerLine) {
        return { entries, errors };
    }
    const headerFields = headerLine.split(',');
    if (headerFields.join(',') !== HEADER) {
        errors.push('CSV header does not match expected format; attempting to map by column position.');
    }
    // Build column index map
    const colIndex = {};
    for (let ci = 0; ci < headerFields.length; ci++) {
        const field = headerFields[ci];
        if (field !== undefined) {
            colIndex[field.trim()] = ci;
        }
    }
    for (let li = 1; li < lines.length; li++) {
        const line = lines[li];
        if (!line || line.trim() === '')
            continue;
        const fields = parseCsvLine(line);
        // Validate required columns exist
        const missing = REQUIRED_COLUMNS.filter((col) => colIndex[col] === undefined || colIndex[col] >= fields.length);
        if (missing.length > 0) {
            errors.push(`Row ${li + 1}: missing required column(s) — ${missing.join(', ')}`);
            continue;
        }
        const tsIdx = colIndex['timestamp'];
        const ksIdx = colIndex['kitSlug'];
        const brIdx = colIndex['brand'];
        const tnIdx = colIndex['testName'];
        const paIdx = colIndex['parameter'];
        const pkIdx = colIndex['parameterKey'];
        const reIdx = colIndex['result'];
        const unIdx = colIndex['unit'];
        const noIdx = colIndex['notes'];
        // All required indices must be defined at this point (validated above)
        if (tsIdx === undefined || ksIdx === undefined || paIdx === undefined ||
            reIdx === undefined || unIdx === undefined) {
            errors.push(`Row ${li + 1}: missing required column index`);
            continue;
        }
        const timestamp = fields[tsIdx]?.trim() ?? '';
        const kitSlug = fields[ksIdx]?.trim() ?? '';
        const brand = (brIdx !== undefined ? fields[brIdx] : '')?.trim() ?? '';
        const testName = (tnIdx !== undefined ? fields[tnIdx] : '')?.trim() ?? '';
        const parameter = fields[paIdx]?.trim() ?? '';
        const parameterKeyRaw = (pkIdx !== undefined ? fields[pkIdx] : '')?.trim() ?? '';
        const resultRaw = fields[reIdx]?.trim() ?? '';
        const unit = fields[unIdx]?.trim() ?? '';
        const notes = (noIdx !== undefined ? fields[noIdx] : '')?.trim() || undefined;
        if (!timestamp || !kitSlug || !parameter || !resultRaw || !unit) {
            errors.push(`Row ${li + 1}: empty required field`);
            continue;
        }
        // Parse result
        let result;
        const resultNum = Number(resultRaw);
        if (isFinite(resultNum) && resultRaw !== '') {
            result = resultNum;
        }
        else {
            result = resultRaw;
        }
        // Generate a unique ID using timestamp + random suffix
        const id = Date.now().toString(36) +
            Math.random().toString(36).slice(2, 10);
        entries.push({
            id,
            date: timestamp,
            kitSlug,
            brand,
            testName,
            parameter,
            parameterKey: parameterKeyRaw,
            result,
            unit,
            notes,
        });
    }
    return { entries, errors };
}
//# sourceMappingURL=csv.js.map